import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultSupplier = () => {
  return {
    _id: '_new',
    number: '_new',
    name: '',
  };
};

const getDefaultState = () => {
  return {
    suppliers: [],
    supplierCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for a supplier
 * used for all queries/mutations which return a supplier
 * @type {DocumentNode}
 */
const SUPPLIER_REQUEST_FIELDS = gql`
  fragment supplierFields on Supplier {
    _id
    number
    name
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} supplier
 */
const createPayload = (supplier) => {
  const payload = {
    name: supplier.name,
  };

  console.log(payload);

  return payload;
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered supplier list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchSuppliersPaginated({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query suppliersPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: SupplierFilterInput
          ) {
            suppliersPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              suppliers {
                ...supplierFields
              }
              supplierCount
            }
          }
          ${SUPPLIER_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      commit('SET_SUPPLIERS', response.data.suppliersPaginated.suppliers);
      commit('SET_SUPPLIER_COUNT', response.data.suppliersPaginated.supplierCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  async fetchSupplierByNumber({ commit, dispatch }, supplierNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query supplierByNumber($supplierNumber: Int!) {
            supplierByNumber(supplierNumber: $supplierNumber) {
              ...supplierFields
            }
          }
          ${SUPPLIER_REQUEST_FIELDS}
        `,
        variables: {
          supplierNumber: parseInt(supplierNumber),
        },
      });
      commit('ADD_OR_UPDATE_SUPPLIER', response.data.supplierByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createSupplier({ commit, dispatch }, supplier) {
    const payload = createPayload(supplier);

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createSupplier($payload: SupplierInput!) {
            createSupplier(supplierInput: $payload) {
              ...supplierFields
            }
          }
          ${SUPPLIER_REQUEST_FIELDS}
        `,
        variables: {
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Lieferant gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_SUPPLIER', '_new'); // remove temporary _new supplier
      commit('ADD_OR_UPDATE_SUPPLIER', response.data.createSupplier);
      return response.data.createSupplier.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async updateSupplier({ commit, dispatch }, supplier) {
    const payload = createPayload(supplier);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateSupplier($supplierNumber: Int!, $payload: SupplierInput!) {
            updateSupplier(supplierNumber: $supplierNumber, supplierInput: $payload) {
              ...supplierFields
            }
          }
          ${SUPPLIER_REQUEST_FIELDS}
        `,
        variables: {
          supplierNumber: supplier.number,
          payload: payload,
        },
      });
      dispatch('message', {
        message: 'Lieferant aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_SUPPLIER', response.data.updateSupplier);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new supplier object in store and return it
   * @param {*} param0
   * @param {*} supplier
   */
  async initSupplier({ commit }) {
    commit('ADD_OR_UPDATE_SUPPLIER', defaultSupplier());
  },

  supplierLocalUpdate({ commit }, supplier) {
    commit('ADD_OR_UPDATE_SUPPLIER', supplier);
  },

  supplierCleanup({ commit }) {
    commit('REMOVE_SUPPLIER', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_SUPPLIER_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_SUPPLIERS: (state, suppliers) =>
    suppliers ? (state.suppliers = suppliers) : (state.suppliers = []),
  ADD_SUPPLIER: (state, supplier) => state.suppliers.push(supplier),
  ADD_OR_UPDATE_SUPPLIER: (state, updatedSupplier) => {
    const existingSupplier = state.suppliers.find(
      (supplier) => supplier._id === updatedSupplier._id
    );
    if (existingSupplier) {
      Object.assign(existingSupplier, updatedSupplier);
    } else {
      state.suppliers.push(updatedSupplier);
    }
  },
  REMOVE_SUPPLIER: (state, supplierId) => {
    const index = state.suppliers.findIndex((supplier) => supplier._id === supplierId);
    state.suppliers.splice(index, 1);
  },
  UPDATE_SUPPLIER: (state, updatedSupplier) => {
    const supplier = state.suppliers.find((supplier) => supplier._id === updatedSupplier._id);
    Object.assign(supplier, updatedSupplier);
  },
  SET_SUPPLIER_COUNT: (state, supplierCount) => (state.supplierCount = supplierCount),
};

/**
 * Getters
 */
const getters = {
  getSuppliers: (state) => state.suppliers,
  getSupplier: (state) => (supplierId) =>
    state.suppliers.find((supplier) => supplier._id == supplierId),
  getSupplierByNumber: (state) => (supplierNumber) =>
    state.suppliers.find((supplier) => supplier.number == supplierNumber),
  getSupplierCount: (state) => state.supplierCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
